/* eslint-disable max-lines */

import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { Subscription, filter } from 'rxjs';
import { WAFExecutionMode, WAFPayloadLimitAction, UnmatchedPathAction, ShieldUpgradeModalComponent, UpdateApiGuardianEndpointRequest } from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { ShieldApiGuardianStore, ShieldApiStore } from '@bn/app-shield/stores';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { DashboardSharedModule } from '@bn/dashboard-shared';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { LazyLoadDirective } from '@bn/web-ui';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartMultiMetricComponent } from '@bn/web-ui/chart/components/charts/chart-multi-metric/chart-multi-metric.component';
import { DroppableContentComponent } from '@bn/web-ui/drop-files/components/droppable-content.component';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { FormListItem } from '@bn/web-ui/models';
import { SelectComponent } from '@bn/web-ui/select/select.component';
import { SideContentComponent } from '@bn/web-ui/side-content/side-content.component';
import { SwitchComponent } from '@bn/web-ui/switch/switch.component';
import { TableLinkClick, TableRow } from '@bn/web-ui/table';
import { TableComponent } from '@bn/web-ui/table/table.component';
import { RadioTabsComponent } from '@bn/web-ui/tabs/radio-tabs.component';
import { TabsComponent } from '@bn/web-ui/tabs/tabs.component';
import { UploadFileComponent } from '@bn/web-ui/upload-file/upload-file.component';
import { tableColumns } from './configs/table-columns.config';
import { enumToSelectItems, flattenParams, paramsToSelectItems, unflattenParams, UPGRADE_MODAL_DATA } from './helpers/injection-detection.helper';
import { createTableRow, getMethodColor } from './helpers/table-data-generator.helper';

@Component({
  templateUrl: './app-shield-api-guardian.page.html',
  styleUrls: ['./app-shield-api-guardian.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DashboardSharedModule,
    BadgeComponent,
    ButtonComponent,
    CardComponent,
    DroppableContentComponent,
    InputComponent,
    RadioTabsComponent,
    SelectComponent,
    SideContentComponent,
    SwitchComponent,
    TableComponent,
    TabsComponent,
    UploadFileComponent,
    ChartMultiMetricComponent,
    LazyLoadDirective,
    NgxSkeletonLoaderComponent,
  ],
})
export class AppShieldApiGuardianPage implements OnDestroy {
  #route = inject(ActivatedRoute);
  #shieldStore = inject(ShieldApiStore);
  #apiGuardianStore = inject(ShieldApiGuardianStore);
  #contentService = inject(ContentService);
  #dialog = inject(BsModalService);

  #subscriptions = new Subscription();
  selectedShield = this.#shieldStore.selectedEntity();
  isAdvanced = computed(() => (this.selectedShield()?.planType ?? PlanType.Basic) >= PlanType.Advanced);

  endpoints = this.#apiGuardianStore.endpoints;
  configuration = this.#apiGuardianStore.configuration;
  chartMetrics = this.#apiGuardianStore.chartMetrics;
  endpointChartMetrics = this.#apiGuardianStore.endpointChartMetrics;
  isLoading = this.#apiGuardianStore.isLoading(StoreRequest.Item);
  isLoaded = this.#apiGuardianStore.isLoaded;
  isUploading = this.#apiGuardianStore.isLoading(StoreRequest.Add);
  isUpdating = this.#apiGuardianStore.isLoading(StoreRequest.Update);
  isUpdatingEnabled = this.#apiGuardianStore.isLoading(StoreRequest.Update, ['isEnabled']);
  isUpdatingExecutionMode = this.#apiGuardianStore.isLoading(StoreRequest.Update, ['executionMode']);
  isUpdatingBodyLimitAction = this.#apiGuardianStore.isLoading(StoreRequest.Update, ['bodyLimitAction']);
  isUpdatingUnmatchedPathAction = this.#apiGuardianStore.isLoading(StoreRequest.Update, ['unmatchedPathAction']);
  isUpdatingOpenApiSpec = this.#apiGuardianStore.isLoading(StoreRequest.Update, ['openApiSpec']);

  tableColumns = tableColumns;

  executionModeItems: FormListItem[] = [
    { label: 'Log', name: 'Log', value: WAFExecutionMode.Log },
    { label: 'Block', name: 'Block', value: WAFExecutionMode.Block },
  ];

  actionItems: FormListItem[] = [
    { label: 'Block', name: 'Block', value: 0 },
    { label: 'Log', name: 'Log', value: 1 },
    { label: 'Ignore', name: 'Ignore', value: 2 },
  ];

  currentTab?: string;
  allTableData: TableRow[] = [];
  filteredTableData: TableRow[] = [];
  tableData: TableRow[] = [];

  currentPage = 1;
  perPage = 20;
  searchTerm = '';
  openApiFiles: File[] = [];
  openApiContent = '';
  selectedEndpointId = signal<number | null>(null);
  sidebarIsOpen = signal(false);

  selectedEndpoint = computed(() => {
    const id = this.selectedEndpointId();
    if (!id) return null;
    return this.endpoints().find((ep) => ep.apiGuardianEndpointId === id) ?? null;
  });

  selectedMethodColor = computed(() => getMethodColor(this.selectedEndpoint()?.requestMethod ?? null));

  isEndpointUpdating = computed(() => {
    const id = this.selectedEndpointId();
    if (!id) return false;
    return this.#apiGuardianStore.isLoading(StoreRequest.Update, [`endpoint-${id}`])();
  });

  authSchemes = computed(() => this.selectedEndpoint()?.authSchemes ?? []);

  availableParamItems = computed(() => paramsToSelectItems(this.selectedEndpoint()?.availableParameters));

  guardianEnums = this.#apiGuardianStore.enums;

  rateLimitTypeItems = computed(() => {
    const items = enumToSelectItems(this.guardianEnums(), 'ApiGuardianRateLimitType', true);
    return items.length > 0
      ? items
      : [
          { Id: 'Global', Name: 'Global' },
          { Id: 'IP', Name: 'IP' },
        ];
  });
  rateLimitTimeframeItems = computed(() => {
    const items = enumToSelectItems(this.guardianEnums(), 'RateLimitTimeframe');
    return items.length > 0
      ? items
      : [
          { Id: 1, Name: '1 second' },
          { Id: 10, Name: '10 seconds' },
          { Id: 60, Name: '1 minute' },
          { Id: 300, Name: '5 minutes' },
          { Id: 900, Name: '15 minutes' },
          { Id: 3600, Name: '1 hour' },
        ];
  });

  endpointFormDirty = signal(false);
  #pendingSave = signal(false);
  endpointForm = {
    isEnabled: true,
    validateRequestBodySchema: false,
    validateResponseBodySchema: false,
    validateAuthorization: false,
    rateLimitingEnabled: false,
    rateLimitingType: null as string | number | null,
    rateLimitingRequestCount: null as number | null,
    rateLimitingTimeframe: null as string | number | null,
    detectParameterXss: false,
    detectParameterSqli: false,
    injectionDetectionParams: [] as string[],
  };

  schemaValidationTab = 0;
  responseSchemaTab = 0;
  schemaValidationTabs = [{ name: 'Request Body' }, { name: 'Response Body' }];

  responseBodyStatusCodes = computed(() => Object.keys(this.selectedEndpoint()?.responseBodySchemasYaml ?? {}).map((code) => ({ name: code })));

  getResponseSchema(): string {
    const codes = this.responseBodyStatusCodes();
    return this.selectedEndpoint()?.responseBodySchemasYaml?.[codes[this.responseSchemaTab]?.name] ?? '';
  }

  constructor() {
    const pullZoneId = this.#shieldStore.pullZoneId();

    this.#contentService.setTitle('API Guardian');
    this.#contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    // set local variable for parent page to be able to use onRouterOutletLoaded
    this.#subscriptions.add(
      this.#route.firstChild?.data.subscribe(({ activeTab }) => {
        this.currentTab = activeTab;
      }),
    );

    let prevIsUpdatingOpenApiSpec = false;
    effect(() => {
      const endpoints = this.endpoints();
      const isUpdatingOpenApiSpec = this.isUpdatingOpenApiSpec();
      this.allTableData = endpoints.map((endpoint) =>
        createTableRow(
          endpoint,
          (endpointId, isEnabled) => this.onToggleEndpoint(endpointId, isEnabled),
          (endpointId) => this.#apiGuardianStore.isLoading(StoreRequest.Update, [`endpoint-${endpointId}`])(),
        ),
      );
      this.filteredTableData = [...this.allTableData];

      if (prevIsUpdatingOpenApiSpec && !isUpdatingOpenApiSpec) {
        this.currentPage = 1;
      }
      prevIsUpdatingOpenApiSpec = isUpdatingOpenApiSpec;

      this.updatePageData();

      if (this.sidebarIsOpen() && this.selectedEndpointId() && this.#pendingSave()) {
        this.#pendingSave.set(false);
        this.initEndpointForm();
      }
    });

    toObservable(this.selectedShield)
      .pipe(filter((shield) => !!shield))
      .pipe(takeUntilDestroyed())
      .subscribe((shield) => {
        const plan = shield?.planType || 0;

        if (plan > PlanType.Basic) {
          const planName = PlanType[plan];

          this.#contentService.setBadge({
            contextType: 'premium',
            text: planName,
            tooltip: `You have Bunny Shield ${planName} enabled`,
          });
        } else {
          this.#contentService.setBadge(null);
        }
      });
  }

  updatePageData(): void {
    const start = (this.currentPage - 1) * this.perPage;
    const end = start + this.perPage;
    this.tableData = this.filteredTableData.slice(start, end);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePageData();
  }

  onSearchChange(term: string): void {
    this.currentPage = 1;
    if (!term) {
      this.filteredTableData = [...this.allTableData];
    } else {
      const searchLower = term.toLowerCase();
      this.filteredTableData = this.allTableData.filter((row) => {
        const searchableText = row.columns
          .map((col) => String(col.value || ''))
          .join(' ')
          .toLowerCase();
        return searchableText.includes(searchLower);
      });
    }
    this.updatePageData();
  }

  onConfigChange(
    data: Partial<{ isEnabled: boolean; executionMode: WAFExecutionMode; bodyLimitAction: WAFPayloadLimitAction; unmatchedPathAction: UnmatchedPathAction }>,
  ): void {
    const shieldZoneId = this.selectedShield()?.shieldZoneId;
    if (!shieldZoneId) return;

    // Determine which field is being updated for loading state tracking
    const customFields = Object.keys(data);
    this.#apiGuardianStore.updateConfiguration({ shieldZoneId, data, customFields });
  }

  onOpenApiFileSelected(files: File[]): void {
    if (!files?.length) return;

    const file = files[0];
    const reader = new FileReader();

    reader.onload = (): void => {
      this.openApiContent = reader.result as string;
      this.openApiFiles = files;
    };

    reader.readAsText(file);
  }

  onOpenApiFileDrop(files: File[]): void {
    if (!files?.length) return;

    const file = files[0];
    const reader = new FileReader();
    const hasEndpoints = this.endpoints().length > 0;
    const shieldZoneId = this.selectedShield()?.shieldZoneId;

    reader.onload = (): void => {
      const content = reader.result as string;

      if (hasEndpoints && shieldZoneId) {
        this.currentPage = 1;
        this.#apiGuardianStore.updateOpenApiSpec({
          shieldZoneId,
          data: { content },
        });
      } else {
        this.openApiContent = content;
        this.openApiFiles = [...files];
      }
    };

    reader.readAsText(file);
  }

  onContinueClick(): void {
    const shieldZoneId = this.selectedShield()?.shieldZoneId;
    if (!shieldZoneId || !this.openApiContent) return;

    const hasEndpoints = this.endpoints().length > 0;

    if (hasEndpoints) {
      this.currentPage = 1;
      this.#apiGuardianStore.updateOpenApiSpec({
        shieldZoneId,
        data: {
          content: this.openApiContent,
        },
      });
    } else {
      this.#apiGuardianStore.uploadOpenApiSpec({
        shieldZoneId,
        data: {
          content: this.openApiContent,
        },
      });
    }

    this.openApiFiles = [];
    this.openApiContent = '';
  }

  onLinkClicked(event: TableLinkClick): void {
    const endpointId = event.itemId as number;
    this.openEndpointSidebar(endpointId);
  }

  private openEndpointSidebar(endpointId: number): void {
    const shieldZoneId = this.selectedShield()?.shieldZoneId;
    this.selectedEndpointId.set(endpointId);
    this.initEndpointForm();
    this.sidebarIsOpen.set(true);
    if (shieldZoneId) {
      this.#apiGuardianStore.getEndpointMetrics({ shieldZoneId, endpointId });
    }
  }

  onToggleEndpoint(endpointId: number, isEnabled: boolean): void {
    const shieldZoneId = this.selectedShield()?.shieldZoneId;
    if (!shieldZoneId) return;

    this.#apiGuardianStore.updateEndpoint({
      shieldZoneId,
      endpointId,
      data: { isEnabled },
      customFields: [`endpoint-${endpointId}`],
    });
  }

  onDropdownClick(event: DropdownMenuItem): void {
    const endpointId = event.id as number;
    const shieldZoneId = this.selectedShield()?.shieldZoneId;

    switch (event.action) {
      case 'edit':
        this.openEndpointSidebar(endpointId);
        break;
      case 'disable':
      case 'enable':
        if (!shieldZoneId) return;
        this.#apiGuardianStore.updateEndpoint({
          shieldZoneId,
          endpointId,
          data: { isEnabled: event.action === 'enable' },
          customFields: [`endpoint-${endpointId}`],
        });
        break;
      default:
        break;
    }
  }

  handleSidebarOpen(value: boolean): void {
    this.sidebarIsOpen.set(value);
    if (!value) {
      this.selectedEndpointId.set(null);
      this.endpointFormDirty.set(false);
    }
  }

  onEndpointToggle(enabled: boolean): void {
    this.endpointForm.isEnabled = enabled;
    this.markFormDirty();
  }

  initEndpointForm(): void {
    const endpoint = this.selectedEndpoint();
    if (!endpoint) return;
    this.endpointForm = {
      isEnabled: endpoint.isEnabled ?? true,
      validateRequestBodySchema: endpoint.validateRequestBodySchema ?? false,
      validateResponseBodySchema: endpoint.validateResponseBodySchema ?? false,
      validateAuthorization: endpoint.validateAuthorization ?? false,
      rateLimitingEnabled: endpoint.rateLimitingEnabled ?? false,
      rateLimitingType: endpoint.rateLimitingType ?? null,
      rateLimitingRequestCount: endpoint.rateLimitingRequestCount ?? null,
      rateLimitingTimeframe: endpoint.rateLimitingTimeframe ?? null,
      detectParameterXss: endpoint.detectParameterXss ?? false,
      detectParameterSqli: endpoint.detectParameterSqli ?? false,
      injectionDetectionParams: flattenParams(endpoint.injectionDetectionParameters),
    };
    this.endpointFormDirty.set(false);
    this.rateLimitingInvalid.set(false);
    this.schemaValidationTab = 0;
    this.responseSchemaTab = 0;
  }

  markFormDirty(): void {
    this.endpointFormDirty.set(true);
  }
  rateLimitingInvalid = signal(false);

  onSaveEndpoint(): void {
    const endpoint = this.selectedEndpoint();
    const shieldZoneId = this.selectedShield()?.shieldZoneId;
    if (!endpoint?.apiGuardianEndpointId || !shieldZoneId) return;

    if (
      this.endpointForm.rateLimitingEnabled &&
      (this.endpointForm.rateLimitingType == null || !this.endpointForm.rateLimitingRequestCount || this.endpointForm.rateLimitingTimeframe == null)
    ) {
      this.rateLimitingInvalid.set(true);
      return;
    }
    this.rateLimitingInvalid.set(false);

    const data: Partial<UpdateApiGuardianEndpointRequest> = {
      isEnabled: this.endpointForm.isEnabled,
      validateRequestBodySchema: this.endpointForm.validateRequestBodySchema,
      validateResponseBodySchema: this.endpointForm.validateResponseBodySchema,
      validateAuthorization: this.endpointForm.validateAuthorization,
      rateLimitingEnabled: this.endpointForm.rateLimitingEnabled,
      detectParameterXss: this.endpointForm.detectParameterXss,
      detectParameterSqli: this.endpointForm.detectParameterSqli,
      injectionDetectionParameters: unflattenParams(this.endpointForm.injectionDetectionParams),
    };

    if (this.endpointForm.rateLimitingEnabled) {
      data.rateLimitingType = this.endpointForm.rateLimitingType as UpdateApiGuardianEndpointRequest['rateLimitingType'];
      data.rateLimitingRequestCount = Number(this.endpointForm.rateLimitingRequestCount);
      data.rateLimitingTimeframe = this.endpointForm.rateLimitingTimeframe as UpdateApiGuardianEndpointRequest['rateLimitingTimeframe'];
    }

    this.#apiGuardianStore.updateEndpoint({
      shieldZoneId,
      endpointId: endpoint.apiGuardianEndpointId,
      data,
      customFields: [`endpoint-${endpoint.apiGuardianEndpointId}`],
    });
    this.endpointFormDirty.set(false);
    this.#pendingSave.set(true);
    this.handleSidebarOpen(false);
  }

  showUpgradeModal(): void {
    const modalRef = this.#dialog.show(ShieldUpgradeModalComponent, {
      class: 'modal-sm modal-md-xl modal-dialog-centered',
      initialState: {
        data: { ...UPGRADE_MODAL_DATA, extra: { ...UPGRADE_MODAL_DATA.extra, requiredPlan: PlanType.Advanced } },
      },
    });

    this.#subscriptions.add(
      modalRef.content?.getOnAction$().subscribe(() => {
        this.#dialog.hide();
      }),
    );
  }

  ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();
  }
}
